import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faCircleExclamation } from '@fortawesome/pro-light-svg-icons/faCircleExclamation';
import { faSpinnerThird } from '@fortawesome/pro-light-svg-icons/faSpinnerThird';
import type { ReleaseSetupItem, ReleaseSongSection } from 'fym-shared/src/music24/releases';
import { TypedMemo } from '../../../../common/types';
import { releaseSetupDetails, releaseSetupItemNames, releaseSetupMessages } from '../setup/releaseSetupMessages';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Overview.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The one-click action of a missing item (QA finding F03), or null: "Fix in Catalog". */
export type SetupMakeAction = 'presave' | 'smartLink' | null;

/**
 * A song with no pre-save page gets "Make the pre-save page"; so does an upcoming song with no
 * SmartLink (its pre-save page is the SmartLink until the release). A released song with no
 * SmartLink gets "Make the SmartLink". A SmartLink that is only a draft is published on its page. Pure.
 */
export function makeActionOf(item: ReleaseSetupItem, isUpcoming: boolean): SetupMakeAction {
  if (item.detail === 'noPresave') {
    return 'presave';
  }
  if (item.detail === 'noSmartLink') {
    return isUpcoming ? 'presave' : 'smartLink';
  }
  return null;
}

interface SetupRowProps {
  item: ReleaseSetupItem;
  onFix: (section: ReleaseSongSection) => void;
  make: SetupMakeAction;
  onMakePresave: () => void;
  onMakeSmartLink: () => void;
}

const SetupRowComponent: React.FC<SetupRowProps> = ({ item, onFix, make, onMakePresave, onMakeSmartLink }) => {
  const intl = useIntl();
  const isOk = item.state === 'ok' || item.state === 'working';
  const fix = useCallback(() => onFix(item.section), [item.section, onFix]);
  let action = (
    <button type="button" className={styles.textLink} onClick={fix}>
      {intl.formatMessage(releaseSetupMessages.fixInCatalog)}
    </button>
  );
  if (make === 'presave') {
    action = (
      <button type="button" className={styles.textLink} onClick={onMakePresave}>
        {intl.formatMessage(releaseSetupMessages.makePresave)}
      </button>
    );
  } else if (make === 'smartLink') {
    action = (
      <button type="button" className={styles.textLink} onClick={onMakeSmartLink}>
        {intl.formatMessage(releaseSetupMessages.makeSmartLink)}
      </button>
    );
  }
  return (
    <div className={styles.missItem}>
      <FontAwesomeIcon
        icon={isOk ? (item.state === 'working' ? faSpinnerThird : faCheck) : faCircleExclamation}
        className={isOk ? styles.okMark : styles.missMark}
        aria-hidden="true"
      />
      <span className={styles.missText}>
        <b className={styles.missName}>{intl.formatMessage(releaseSetupItemNames[item.key])}</b>
        {item.detail !== null && (
          <small className={styles.missDetail}>{intl.formatMessage(releaseSetupDetails[item.detail])}</small>
        )}
      </span>
      {!isOk && action}
    </div>
  );
};

export const SetupRow = TypedMemo(SetupRowComponent);
